import React, { useState } from "react";
import { RootDispatch } from "../../store";
import { useDispatch } from "react-redux";
import { handleAddUsers } from "../../store/slices/userSlice";
import toast from "react-hot-toast";
import Button, { ButtonType } from "../../components/Assets/Button";
import { IUser } from "../../models/userData.model";
import { NewUser } from "./UserManagement";
import { IStateDistrict } from "../../models/LeadData.model";
import NavigationAccessModal from "../../components/NavigationAccessModal";
import LocationSelector from "../../components/Users/LocationSelector";
import ToggleSwitch from "../../components/Users/ToggleSwitch";
import { Eye, EyeOff } from "lucide-react";

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  newUser: NewUser;
  setNewUser: React.Dispatch<React.SetStateAction<NewUser>>;
  roles: string[];
  users: IUser[];
  warehouses: any[];
  statesData: IStateDistrict[];
  getManagerOptions: () => IUser[];
  handleRoleChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  newUser,
  setNewUser,

  roles,
  warehouses,
  statesData,
  getManagerOptions,
  handleRoleChange,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);

  const hideLocations = [
    "admin",
    "factory",
    "account",
    "data entry",
    "sp",
    "hr",
    "LE",
    "CSE",
    "digitalMarketing",
  ];

  const displayUserName = (role: string) => {
    if (role === "sh") {
      return "Sales Head";
    }
    if (role === "digitalMarketing") {
      return "Digital Marketing";
    }
    if (role === "bdm" || role === "bde" || role === "hr" || role === "LE") {
      return role.toUpperCase();
    }
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  const [validationMessages, setValidationMessages] = useState({
    username: "",
    email: "",
    password: "",
    mobileNumber: "",
    role: "",
  });

  const validateFields = (): boolean => {
    setValidationMessages({
      username: "",
      email: "",
      password: "",
      mobileNumber: "",
      role: "",
    });

    if (!newUser?.username?.trim()) {
      setValidationMessages((prev) => ({
        ...prev,
        username: "Username is required",
      }));
      toast.error("Username is required");
      return false;
    }

    if (!newUser?.email?.trim()) {
      setValidationMessages((prev) => ({
        ...prev,
        email: "Email is required",
      }));
      toast.error("Email is required");
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(newUser.email)) {
      setValidationMessages((prev) => ({
        ...prev,
        email: "Please enter a valid email",
      }));
      toast.error("Please enter a valid email address");
      return false;
    }

    if (!newUser?.password?.trim()) {
      setValidationMessages((prev) => ({
        ...prev,
        password: "Password is required",
      }));
      toast.error("Password is required");
      return false;
    }

    if (newUser.password.length < 6) {
      setValidationMessages((prev) => ({
        ...prev,
        password: "Min 6 characters required",
      }));
      toast.error("Password must be at least 6 characters long");
      return false;
    }

    const mobileNum = newUser?.mobileNumber || "";
    if (!mobileNum.trim()) {
      setValidationMessages((prev) => ({
        ...prev,
        mobileNumber: "Mobile number is required",
      }));
      toast.error("Mobile number is required");
      return false;
    }

    if (mobileNum.length !== 10) {
      setValidationMessages((prev) => ({
        ...prev,
        mobileNumber: "Enter valid 10-digit number",
      }));
      toast.error("Please enter a valid 10-digit mobile number");
      return false;
    }

    if (!newUser?.role) {
      setValidationMessages((prev) => ({
        ...prev,
        role: "Please select a role",
      }));
      toast.error("Please select a role");
      return false;
    }

    return true;
  };
  const onHandleAddUser = async () => {
    if (!validateFields()) return;

    try {
      await dispatch(handleAddUsers(newUser)).unwrap();
      onClose();
    } catch (err: any) {
      console.error(err || "Failed to add user");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto">
        <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl my-8 mx-4">
          <div className="p-6 md:p-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                <span className="w-1 h-6 bg-navyBlue-primary mr-3 rounded-full" />
                Add User
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <input
                  type="text"
                  placeholder="Username"
                  value={newUser?.username}
                  onChange={(e) =>
                    setNewUser({ ...newUser, username: e.target.value })
                  }
                  className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
              </div>

              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={newUser?.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
              </div>

              <div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={newUser?.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full pr-10"
                  />
                  <div
                    className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <Eye className="text-gray-600 h-4 w-4" />
                    ) : (
                      <EyeOff className="text-gray-600 h-4 w-4" />
                    )}
                  </div>
                </div>
              </div>

              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]*"
                  placeholder="Mobile Number"
                  value={newUser.mobileNumber}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    setNewUser({ ...newUser, mobileNumber: numericValue });
                  }}
                  className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                />
              </div>

              <div>
                <select
                  value={newUser.role}
                  onChange={handleRoleChange}
                  className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                >
                  <option value="" disabled>
                    Select Role
                  </option>
                  {roles &&
                    roles.map((role, index) => (
                      <option key={index} value={role}>
                        {displayUserName(role)}
                      </option>
                    ))}
                </select>
              </div>

              {["factory", "floor", "store"].includes(newUser.role) && (
                <div>
                  <select
                    value={newUser.warehouseAssigned}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        warehouseAssigned: e.target.value,
                      })
                    }
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  >
                    <option value="" disabled>
                      Select Factory
                    </option>
                    {warehouses.map((warehouse: any) => (
                      <option key={warehouse._id} value={warehouse._id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {newUser.role && newUser.role !== "admin" && (
                <div>
                  <select
                    value={newUser.manager}
                    onChange={(e) =>
                      setNewUser({ ...newUser, manager: e.target.value })
                    }
                    className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
                  >
                    <option value="" disabled>
                      Reporting To
                    </option>
                    {getManagerOptions().map((user: IUser) => (
                      <option key={user._id} value={user._id}>
                        {user.username} ({displayUserName(user.role)})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {newUser.role &&
                !hideLocations.includes(newUser.role) &&
                !["store", "floor"].includes(newUser.role) && (
                  <div className="md:col-span-2">
                    <LocationSelector
                      role={newUser.role}
                      states={newUser.states}
                      districts={newUser.districts}
                      onStatesChange={(states) =>
                        setNewUser((prev) => ({ ...prev, states }))
                      }
                      onDistrictsChange={(districts) =>
                        setNewUser((prev) => ({ ...prev, districts }))
                      }
                      statesData={statesData}
                    />
                  </div>
                )}
            </div>

            <div className="my-4">
              <Button
                type={ButtonType.Cancel}
                onClick={() => setIsAccessModalOpen(true)}
              >
                Select Access
              </Button>
            </div>

            <div className="space-y-3">
              <ToggleSwitch
                id="geoLocationToggle"
                checked={newUser.isGeoLocationEnabled}
                onChange={(checked: boolean) =>
                  setNewUser((prev) => ({
                    ...prev,
                    isGeoLocationEnabled: checked,
                  }))
                }
                sizeVariant="sm"
                label="Enable Geo Location"
              />
              <ToggleSwitch
                id="trackLocationToggle"
                checked={newUser.trackLocation}
                onChange={(checked: boolean) =>
                  setNewUser((prev) => ({ ...prev, trackLocation: checked }))
                }
                sizeVariant="sm"
                label="Enable GPS Tracking"
                description="Allow real-time GPS tracking for this user"
              />
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <Button type={ButtonType.Cancel} onClick={onClose}>
                Cancel
              </Button>
              <Button type={ButtonType.Submit} onClick={onHandleAddUser}>
                Add User
              </Button>
            </div>
          </div>
        </div>
      </div>

      <NavigationAccessModal
        isOpen={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        navigations={newUser.navigations}
        setNavigations={(nav) =>
          setNewUser((prev) => ({
            ...prev,
            navigations: nav,
          }))
        }
      />
    </>
  );
};

export default AddUserModal;
